import { jwtDecode } from 'jwt-decode';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api/client';
import { EMPLOYEE_STORAGE_KEY, TOKEN_STORAGE_KEY } from '../config';
import type { Employee, EmployeeRole, JwtPayload } from '../types/auth';
import { isEmployeeRole } from '../types/auth';

type AuthContextValue = {
  token: string | null;
  employee: Employee | null;
  role: EmployeeRole | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function employeeFromToken(token: string): Employee | null {
  try {
    const payload = jwtDecode<JwtPayload>(token);
    if (!payload.sub || !payload.email || !payload.role || !isEmployeeRole(payload.role)) return null;
    return {
      id: payload.sub,
      email: payload.email,
      role: payload.role,
      name: payload.email.split('@')[0] ?? payload.email,
    };
  } catch {
    return null;
  }
}

function readStoredEmployee(token: string): Employee | null {
  try {
    const raw = localStorage.getItem(EMPLOYEE_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Employee;
      if (parsed.id && parsed.email && parsed.role) return parsed;
    }
  } catch {
    /* ignore */
  }
  return employeeFromToken(token);
}

function readStoredToken(): string | null {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) return null;
  const employee = readStoredEmployee(token);
  if (!employee) {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(EMPLOYEE_STORAGE_KEY);
    return null;
  }
  return token;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => readStoredToken());
  const [employee, setEmployee] = useState<Employee | null>(() =>
    token ? readStoredEmployee(token) : null,
  );

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.login(email.trim().toLowerCase(), password);
    if (!res.ok || !res.token) {
      throw new Error(res.error || 'Login failed');
    }
    const emp = res.employee ?? employeeFromToken(res.token);
    localStorage.setItem(TOKEN_STORAGE_KEY, res.token);
    if (emp) localStorage.setItem(EMPLOYEE_STORAGE_KEY, JSON.stringify(emp));
    setToken(res.token);
    setEmployee(emp);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(EMPLOYEE_STORAGE_KEY);
    setToken(null);
    setEmployee(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      employee,
      role: employee?.role ?? null,
      isAuthenticated: Boolean(token && employee),
      login,
      logout,
    }),
    [token, employee, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
